import { ContactUs } from "@App/Components/ClientSpecific/Raiffeisen/ContactUs";
import { FlipCard } from "@App/Components/ClientSpecific/Raiffeisen/FlipCard";
import { BulletPoint } from "@App/Components/common/BulletPoint";
import {
  BonusBenefitsIcon,
  CalendarCheckBenefitsIcon,
  CheckBenefitsIcon,
} from "@App/Components/common/Icons";
import { BrandFilter } from "@App/Components/Filters";
import { Hero } from "@App/Components/Hero";
import { Loader } from "@App/Components/Loader";
import { Modal } from "@App/Components/Modal";
import { HomeSections } from "@App/Components/molecules/whitelabel/home/sections/HomeSections";
import { LocationStepDefinition } from "@App/Components/molecules/whitelabel/home/stepDefinitions/LocationStepDefinition";
import { Step } from "@App/Components/Stepper/Step";
import { Stepper } from "@App/Components/Stepper/Stepper";
import { TriangleDivider } from "@App/Components/TriangleDivider";
import { useServices } from "@App/hooks/api/useServices";
import { useHome } from "@App/hooks/useHome";
import { useLocationStepDefinition } from "@App/hooks/whitelabel/home/stepper/useLocationStepDefinition";
import { EngineTypeStep } from "@App/Pages/Partner/Partials/EngineTypeStep";
import { SecondaryTireServiceStep } from "@App/Pages/Partner/Partials/SecondaryTireServiceStep";
import { ServiceStep } from "@App/Pages/Partner/Partials/ServiceStep";
import { SubServiceStep } from "@App/Pages/Partner/Partials/SubServiceStep";
import { TireServiceStep } from "@App/Pages/Partner/Partials/TireServiceStep";
import {
  ServiceIsInspectionOrServiceIsAppointmentOrServiceIsCertificate,
  ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored,
} from "@App/utils/conditionals/combinations";
import { ServiceIsInspection } from "@App/utils/conditionals/services";
import { Fragment, type FunctionComponent, useEffect } from "react";
import { useTranslation } from "react-i18next";

export type HomeProps = {
  embedded?: boolean;
  onSubmit: () => void;
};

export const Home: FunctionComponent<HomeProps> = ({ embedded = false, onSubmit }: HomeProps) => {
  const { t } = useTranslation();

  const locationStepProps = useLocationStepDefinition(onSubmit);

  const { loading, error, services } = useServices();

  const {
    currentStep,
    setCurrentStep,
    selectBrand,
    selectEngine,
    togglePremiumService,
    togglePremiumServiceCarReplacementOrPickup,
    toggled,
    nextStep,
    organization,
    service,
    availableSubservices,
    availableSubservicesLoading,
    premiumServices,
    selectService,
    selectTireServiceTypeAndService,
    secondaryTireStepSelection,
    tireStepSelection,
    showSecondaryTireStep,
    bevSupport,
    brand,
  } = useHome();

  useEffect(() => {
    if (currentStep === 0 && service) {
      nextStep();
    }
  }, [service]);

  if (error) {
    console.log(error);
  }

  return (
    <Fragment>
      <Hero
        organization={organization}
        title={t("hero.title", { organization: organization.name })}
        subtitle={t("hero.subtitle", {
          organization: organization.name,
        })}
        backgroundImage={
          organization.settings.hero.background_image_url ??
          "/images/brand/arval-banner-darkened.jpg"
        }
        backgroundPosition={"bg-center"}
        mode="dark"
      />
      <Modal
        open={organization.settings.welcome_modal.display ?? true}
        onClick={() => null}
        title={t("welcomeModal.title", {
          organization: organization.name,
        })}
        buttonText={"Weiter"}
        width="max-w-3xl"
      >
        <div>
          {t("welcomeModal.text", {
            organization: organization.name,
          })}
        </div>
      </Modal>
      <div className="bg-white px-5 pt-5 container">
        <Stepper currentStep={currentStep} onChange={setCurrentStep}>
          <Step
            title={t("Waehlen Sie einen Service.")}
            subtitle={service?.pivot.subtitle ? service.pivot.subtitle : null}
            completedTitle={"Service: "}
            completedGoBackLinkTitle={
              service
                ? service.pivot.display_name
                  ? service.pivot.display_name
                  : service.name
                : ""
            }
          >
            <ServiceStep
              service={service}
              services={services}
              loading={loading}
              error={error}
              selectService={selectService}
              organization={organization}
            />
          </Step>
          {availableSubservicesLoading && <Loader />}
          {ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored(service) &&
            !availableSubservicesLoading && (
              <Step
                title={"Welchen Service benötigen Sie?"}
                subtitle={null}
                completedTitle={"Reifenservice" + ": "}
                completedGoBackLinkTitle={tireStepSelection}
              >
                <TireServiceStep
                  services={services}
                  selectService={selectTireServiceTypeAndService}
                  organization={organization}
                  secondary={false}
                />
              </Step>
            )}
          {showSecondaryTireStep &&
            ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored(service) &&
            !availableSubservicesLoading && (
              <Step
                title={"Sind die Reifen eingelagert?"}
                subtitle={null}
                completedTitle={"Sind die Reifen eingelagert?" + ": "}
                completedGoBackLinkTitle={secondaryTireStepSelection}
              >
                <SecondaryTireServiceStep
                  services={services}
                  selectService={selectTireServiceTypeAndService}
                  organization={organization}
                  secondary={true}
                />
              </Step>
            )}
          {(availableSubservices?.length > 0 || service == null) &&
            !availableSubservicesLoading && (
              <Step
                title={t("chooseSubServices")}
                subtitle={null}
                completedTitle={"Services: "}
                completedGoBackLinkTitle={`${premiumServices?.length === 0 ? "Keine Auswahl" : ""} ${premiumServices?.map((selected) => `${selected.name}`)}`}
              >
                <SubServiceStep
                  availableSubservices={availableSubservices}
                  premiumServices={premiumServices ?? []}
                  toggled={toggled}
                  togglePremiumServiceCarReplacementOrPickup={
                    togglePremiumServiceCarReplacementOrPickup
                  }
                  togglePremiumService={togglePremiumService}
                  nextStep={nextStep}
                />
              </Step>
            )}
          {ServiceIsInspection(service) && !availableSubservicesLoading && (
            <Step
              title={t("Antriebsart")}
              subtitle={null}
              completedTitle={t("Antriebsart") + ": "}
              completedGoBackLinkTitle={
                bevSupport ? t("Elektro-/Hybridmotor") : t("Verbrennungsmotor")
              }
            >
              <EngineTypeStep selectEngine={selectEngine} />
            </Step>
          )}
          {service &&
            ServiceIsInspectionOrServiceIsAppointmentOrServiceIsCertificate(service) &&
            !availableSubservicesLoading && (
              <Step
                title={t("Waehlen Sie Ihre Marke.")}
                completedTitle={"Marke: "}
                completedGoBackLinkTitle={brand ? brand.name : ""}
              >
                <Fragment>
                  <BrandFilter onSubmit={selectBrand} />
                </Fragment>
              </Step>
            )}
          <LocationStepDefinition {...locationStepProps} />
        </Stepper>
      </div>
      <section className={"container px-5 my-5 flex flex-col gap-4"}>
        <h2 className={"font-serif text-2xl"}>Ihre Servicekarte*</h2>
        <FlipCard
          front={{
            src: "/images/partner/raiffeisen/servicekarte-front.jpg",
            alt: "Servicekarte",
          }}
          back={{
            src: "/images/partner/raiffeisen/servicekarte-back.jpg",
            alt: "Servicekarte",
          }}
        />
        <a download href={"/images/partner/raiffeisen/Servicekarte 85x55 RIL Fuhrpark.pdf"}>
          Herunterladen*
        </a>
      </section>
      <HomeSections />
      {!embedded && (
        <div className="bg-neutral-100">
          <TriangleDivider direction="left" background={"bg-white"} />
          <div className="px-5 py-5 container md:pb-20">
            <div className="md:grid md:grid-cols-[50%_50%]">
              <div className="md:pr-12">
                <div className="font-serif text-2xl mb-4">{t("benefits.title")}</div>
                <BulletPoint
                  text={t("benefits.bullet-point-1", {
                    organization: organization.name,
                  })}
                >
                  <CalendarCheckBenefitsIcon />
                </BulletPoint>
                <BulletPoint
                  text={t("benefits.bullet-point-2", {
                    organization: organization.name,
                  })}
                >
                  <CheckBenefitsIcon />
                </BulletPoint>
                <BulletPoint
                  text={t("benefits.bullet-point-3", {
                    organization: organization.name,
                  })}
                >
                  <BonusBenefitsIcon />
                </BulletPoint>
              </div>
              <div className="pt-5 md:pt-0 md:ml-12">
                {/*custom component*/}
                <ContactUs />
              </div>
            </div>
          </div>
        </div>
      )}
      <section className={"container px-5 my-5"}>
        <p className={"text-xs"}>
          <span className={"font-bold"}>
            * Rechtlicher Hinweis zur Nutzung der digitalen Servicekarte während der
            Leasingvertragslaufzeit
          </span>
          <br /> Die Nutzung der digitalen Servicekarte ist ausschließlich während der Laufzeit des
          Leasingvertrags für den Einkauf von Dienstleistungen berechtigt. Nach Ablauf der
          Leasingvertragslaufzeit erlischt das Recht zur Nutzung der digitalen Servicekarte
          automatisch. Jegliche Nutzung der digitalen Servicekarte außerhalb der vertraglich
          festgelegten Leasingdauer ist untersagt und kann rechtliche Konsequenzen nach sich ziehen.
          Der Leasingnehmer ist verpflichtet, die Bestimmungen des Leasingvertrags strikt
          einzuhalten und die digitale Servicekarte nach Ablauf der Leasingvertragslaufzeit nicht
          weiter zu verwenden. Bei Zuwiderhandlungen behält sich der Leasinggeber das Recht vor,
          entsprechende rechtliche Schritte einzuleiten.
        </p>
      </section>
    </Fragment>
  );
};
